import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Search, ShieldCheck, Tv,
} from "lucide-react";
import { toast } from "sonner";

// Channel admin picks a PH project by ID (later: search) and files a
// bind-request. Backend enforces:
//   • TV-typed project only
//   • 30-day rejection cool-down
//   • No duplicate active/pending desk per (channel, project)
export default function AttachShowDesk() {
  const nav = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const lookup = async () => {
    if (!projectId.trim()) return;
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.get(`/projects/${projectId.trim()}`);
      setPreview(data.project);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Project not found");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/show-desks/request", {
        project_id: projectId.trim(), note: note.trim(),
      });
      toast.success("Request sent to the Production House");
      nav(`/channel/show-desks/${data.desk.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not request");
    } finally {
      setSubmitting(false);
    }
  };

  const isTV = preview && ["tv_episodic", "tv_daily_soap", "format_reality",
                           "tv", "format_show"].includes(preview.project_type);

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10"
      data-testid="attach-show-desk-page">
      <button onClick={() => nav("/channel/show-desks")}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5 mb-3"
        data-testid="attach-back-btn">
        <ArrowLeft size={12} /> Back to Show Desks
      </button>

      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        Channel / OTT
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
        Attach a Show Desk
      </h1>
      <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
        Enter the Production House&apos;s Project ID for the show your channel
        commissions. The PH owner receives a request and approves it before
        your programming team gets scoped read access.
      </p>

      <div className="mt-8 space-y-6">
        <div>
          <Label className="text-[10px] tracking-[0.24em] uppercase text-white/55">
            Project ID *
          </Label>
          <div className="mt-2 flex gap-2">
            <Input value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="e.g. 6ec78956-4cfd-435b-b85f-b54395403eac"
              className="h-11 bg-black/40 border-white/15 font-mono text-sm"
              data-testid="attach-project-id-input" />
            <Button onClick={lookup} disabled={loading || !projectId.trim()}
              variant="outline"
              className="h-11 px-4 border-white/15 hover:bg-white/5"
              data-testid="attach-lookup-btn">
              <Search size={14} className="mr-1.5" />
              {loading ? "Looking up…" : "Look up"}
            </Button>
          </div>
        </div>

        {preview && (
          <div className={"cs-card rounded-2xl p-5 border " +
            (isTV ? "border-emerald-500/25" : "border-red-500/25")}
            data-testid="attach-preview-card">
            <div className="flex items-center gap-2">
              {isTV
                ? <ShieldCheck size={16} className="text-emerald-300" />
                : <Tv size={16} className="text-red-300" />}
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
                {preview.project_type?.replace(/_/g, " ") || "unknown type"}
              </div>
            </div>
            <div className="font-display text-2xl uppercase tracking-tight mt-2">
              {preview.title}
            </div>
            {!isTV && (
              <div className="mt-3 text-xs text-red-300"
                data-testid="attach-preview-not-tv">
                Show Desks can only attach to TV-typed projects (Episodic ·
                Daily Soap · Format/Reality). Ask the PH to convert the
                project type first.
              </div>
            )}
          </div>
        )}

        <div>
          <Label className="text-[10px] tracking-[0.24em] uppercase text-white/55">
            Note to the Production House (optional)
          </Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Introduce your programming team and why you'd like the desk attached."
            className="mt-2 bg-black/40 border-white/15 text-sm"
            data-testid="attach-note-input" />
        </div>

        <div className="flex justify-end">
          <Button onClick={submit} disabled={submitting || !isTV}
            className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="attach-submit-btn">
            {submitting ? "Sending…" : "Send request"}
          </Button>
        </div>
      </div>
    </div>
  );
}
